let sidebar;
let element;
let desktopMedia;

let lastScrollTop = 0;
let windowHeight = 0;
let sidebarHeight = 0;

const topGap = 85;
const topGapText = `${topGap}px`;

const bottomGap = 20;
const bottomGapText = `${bottomGap}px`;

// classes a modal puts on <html> to lock the page scroll (see BaseModal.vue `stopScrollClass`)
const scrollLockClasses = ['disabled-scroll', 'overflow-hidden'];
let isFrozen = false;
let wasStickyBeforeFreeze = false;

function enableAbsoluteCalculatedPosition(top) {
  element.style.top = top;
  element.style.bottom = 'auto';

  element.classList.remove('sticky');
  element.classList.remove('fixed');
  element.classList.add('absolute');
  element.classList.remove('static');
}
function enableAbsoluteBottomPosition() {
  element.classList.remove('sticky');
  element.classList.remove('fixed');
  element.classList.add('absolute');
  element.classList.remove('static');
  element.style.top = 'auto';
  element.style.bottom = bottomGapText;
}
function enableFixedTopPosition() {
  element.classList.remove('sticky');
  element.classList.add('fixed');
  element.classList.remove('absolute');
  element.classList.remove('static');
  element.style.top = topGapText;
  element.style.bottom = 'auto';
}
function enableFixedBottomPosition() {
  element.classList.remove('sticky');
  element.classList.add('fixed');
  element.classList.remove('absolute');
  element.classList.remove('static');
  element.style.top = 'auto';
  element.style.bottom = bottomGapText;
}
function enableStaticPosition() {
  element.classList.remove('sticky');
  element.classList.remove('fixed');
  element.classList.remove('absolute');
  element.classList.add('static');
  element.style.top = 'auto';
  element.style.bottom = 'auto';
}

function enableStickyPosition() {
  element.classList.add('sticky');
  element.classList.remove('fixed');
  element.classList.remove('static');
  element.classList.remove('absolute');
  element.style.top = topGapText;
  element.style.bottom = 'auto';
}

function isPageScrollLocked() {
  return scrollLockClasses.some((className) => document.documentElement.classList.contains(className));
}

function getContentOffset() {
  const content = element.firstElementChild;
  return content ? content.getBoundingClientRect().top - element.getBoundingClientRect().top : 0;
}

/**
 * A modal locks the page scroll, which leaves the form without a scrollport and makes the browser
 * drop a `sticky` element back to its position in the document flow - out of sight above the
 * viewport on a scrolled page. `sticky` and `fixed` also open a stacking context, which traps the
 * enquiry modal rendered inside the form under the page's fixed navigation. Freezing the form as
 * `absolute` in the sidebar at the coordinates it occupies right now avoids both: `absolute` is
 * unaffected by the lock and opens no stacking context. `absolute` and `static` need neither.
 *
 * The lock is already applied when this runs, so a sticky form measures at its flow position and
 * its sticky offset has to be applied by hand - the same clamp the browser would do.
 */
function freezePosition() {
  if (isFrozen || !desktopMedia.matches) {
    return;
  }

  wasStickyBeforeFreeze = element.classList.contains('sticky');
  if (!wasStickyBeforeFreeze && !element.classList.contains('fixed')) {
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const sidebarRect = sidebar.getBoundingClientRect();
  const viewportTop = wasStickyBeforeFreeze
    ? Math.min(Math.max(elementRect.top, topGap), sidebarRect.bottom - elementRect.height)
    : elementRect.top;
  const contentOffsetBeforeFreeze = getContentOffset();

  enableAbsoluteCalculatedPosition(`${viewportTop - sidebarRect.top}px`);

  // out of the flow the form no longer collapses its content's top margin into the page, which
  // moves the visible card down by that margin - take it back out of the frozen offset
  const collapsedMargin = getContentOffset() - contentOffsetBeforeFreeze;
  element.style.top = `${viewportTop - sidebarRect.top - collapsedMargin}px`;
  isFrozen = true;
}

/**
 * A form frozen out of `fixed` is left `absolute`: that is the same hand-off onScroll makes on every
 * change of scroll direction, so the next scroll event carries on from there.
 */
function unfreezePosition() {
  if (!isFrozen) {
    return;
  }

  isFrozen = false;
  if (wasStickyBeforeFreeze) {
    enableStickyPosition();
  }
}

function observePageScrollLock() {
  const observer = new MutationObserver(() => {
    if (isPageScrollLocked()) {
      freezePosition();
    } else {
      unfreezePosition();
    }
  });

  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
}

function onScroll() {
  // a locked page is pinned at scrollTop 0, and repositioning the form from that would drop it
  // out of view behind the modal
  if (!desktopMedia.matches || isPageScrollLocked()) {
    return;
  }

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const isScrollUp = scrollTop < lastScrollTop;
  const bottomViewportEdge = scrollTop + windowHeight;
  const elementOffsetTop = element.offsetTop;
  const sidebarOffsetTop = sidebar.offsetTop;
  const elementHeight = element.scrollHeight;

  lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

  if (elementHeight + topGap < windowHeight) {
    // elementHeight is smaller than viewport height
    enableStickyPosition();
    return;
  }
  // elementHeight is bigger than viewport height

  if (bottomViewportEdge > sidebarOffsetTop + sidebarHeight) {
    // we are next to footer, fix the element to bottom of the sidebar (absolute position)
    enableAbsoluteBottomPosition();
    return;
  }

  if (bottomViewportEdge < sidebarOffsetTop + elementHeight) {
    //  we are above element offsetTop, we need to use static position
    if (element.classList.contains('fixed')) {
      if (scrollTop < sidebarOffsetTop - topGap) {
        enableStaticPosition();
      }
    } else {
      // we are on the top of the page (top part of element is visible)
      enableStaticPosition();
    }
    return;
  }

  // we are in the middle, fix the element to bottom of the screen (fixed position) or use absolute position
  if (!isScrollUp) {
    // scrolling down
    if (element.classList.contains('fixed') && element.style.bottom === 'auto') {
      // fix the element to the sidebar by absolute position
      enableAbsoluteCalculatedPosition(`${elementOffsetTop + scrollTop - sidebarOffsetTop}px`);
    } else if (window.scrollY + windowHeight > sidebarOffsetTop + element.scrollHeight + elementOffsetTop + bottomGap) {
      enableFixedBottomPosition();
    } else if (element.classList.contains('sticky')) {
      enableFixedTopPosition();
    }

    return;
  }

  // scrolling up
  if (!element.classList.contains('absolute') && element.style.top !== topGapText) {
    // fix the element to the sidebar by absolute position
    enableAbsoluteCalculatedPosition(`${elementOffsetTop + scrollTop - sidebarOffsetTop}px`);
  } else if (scrollTop < elementOffsetTop + sidebarOffsetTop - topGap) {
    enableFixedTopPosition();
  }
}
function updateVariables() {
  windowHeight = window.innerHeight;
  sidebarHeight = sidebar.scrollHeight;
}
export default function init() {
  sidebar = document.getElementById('col-sidebar');
  element = document.getElementById('desktop-enquiry-form');
  desktopMedia = window.matchMedia('(min-width: 1280px)');

  updateVariables();

  window.addEventListener('resize', updateVariables);
  document.addEventListener('scroll', onScroll, { passive: true });
  observePageScrollLock();
}
